import { CORE_PRODUCT_REGISTRY } from "@clickdialog/product-registry";

export const CASA_NATIVA_PUBLIC_PATH = "/demos/gastronomia/casa-nativa" as const;

export type AppRoute =
  | { kind: "login"; returnTo: string | null }
  | { kind: "account" }
  | { kind: "select-organization" }
  | { kind: "demos" }
  | { kind: "demo-detail"; demoId: string; moduleKey: string | null; nestedPath: string }
  | { kind: "prospect-demo"; requestId: string }
  | { kind: "solution-studio" }
  | { kind: "casa-nativa-public" }
  | { kind: "crm"; section: "home" | "businesses" | "business" | "pipeline" | "tasks"; businessId?: string }
  | { kind: "organization"; slug: string; section: "home" | "settings" | "product"; productRoute?: string; nestedPath: string }
  | { kind: "not-found" };

const PRIVATE_PATH = /^\/(?:account|select-organization|demos(?:\/|$)|solution-studio(?:\/|$)|crm(?:\/|$)|o\/)/;
const SLUG = /^[a-z0-9](?:[a-z0-9-]{0,62})$/;
const CRM_ID = /^cd_crm_[a-f0-9]{32}$/;
const CORE_PRODUCT_ROUTES = Object.freeze(
  CORE_PRODUCT_REGISTRY.manifests.map((manifest) => manifest.routeBase),
);

export function safeReturnPath(value: string | null | undefined): string | null {
  if (!value || !value.startsWith("/") || value.startsWith("//") || !PRIVATE_PATH.test(value)) return null;
  try {
    const parsed = new URL(value, "https://app.clickdialog.com");
    if (parsed.origin !== "https://app.clickdialog.com") return null;
    if (parsed.pathname === CASA_NATIVA_PUBLIC_PATH) return null;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

export function parseRoute(pathname: string, search = "", productRoutes: readonly string[] = CORE_PRODUCT_ROUTES): AppRoute {
  if (pathname === "/") return { kind: "select-organization" };
  if (pathname === "/login") return { kind: "login", returnTo: safeReturnPath(new URLSearchParams(search).get("returnTo")) };
  if (pathname === "/account") return { kind: "account" };
  if (pathname === "/select-organization") return { kind: "select-organization" };
  if (pathname === CASA_NATIVA_PUBLIC_PATH) return { kind: "casa-nativa-public" };
  if (pathname === "/demos") return { kind: "demos" };
  const prospectDemo = pathname.match(
    /^\/demos\/prospects\/(cd_pdr_[a-f0-9]{32})$/,
  );
  if (prospectDemo) {
    return { kind: "prospect-demo", requestId: prospectDemo[1]! };
  }
  if (pathname.startsWith("/demos/prospects/")) return { kind: "not-found" };
  const demoDetail = pathname.match(/^\/demos\/([^/]+)(?:\/([^/]+)(\/.*)?)?$/);
  if (demoDetail) {
    return {
      kind: "demo-detail",
      demoId: decodeURIComponent(demoDetail[1]!),
      moduleKey: demoDetail[2] ? decodeURIComponent(demoDetail[2]) : null,
      nestedPath: demoDetail[3] ?? "",
    };
  }
  if (pathname.startsWith("/demos/")) return { kind: "not-found" };
  if (pathname === "/solution-studio") return { kind: "solution-studio" };
  if (pathname.startsWith("/solution-studio/")) return { kind: "not-found" };

  if (pathname === "/crm") return { kind: "crm", section: "home" };
  if (pathname === "/crm/businesses") return { kind: "crm", section: "businesses" };
  if (pathname === "/crm/pipeline") return { kind: "crm", section: "pipeline" };
  if (pathname === "/crm/tasks") return { kind: "crm", section: "tasks" };
  const crmBusiness = pathname.match(/^\/crm\/businesses\/([^/]+)$/);
  if (crmBusiness && CRM_ID.test(crmBusiness[1]!)) return { kind: "crm", section: "business", businessId: crmBusiness[1]! };
  if (pathname.startsWith("/crm/")) return { kind: "not-found" };

  const match = pathname.match(/^\/o\/([^/]+)\/([^/]+)(\/.*)?$/);
  if (!match || !SLUG.test(match[1]!)) return { kind: "not-found" };
  const [, slug, section, remainder = ""] = match;
  if (section === "home" || section === "settings") {
    return remainder === "" ? { kind: "organization", slug: slug!, section, nestedPath: "" } : { kind: "not-found" };
  }
  if (!productRoutes.includes(section!)) return { kind: "not-found" };
  return { kind: "organization", slug: slug!, section: "product", productRoute: section, nestedPath: remainder };
}

export function canonicalOrganizationPath(pathname: string, canonicalSlug: string): string {
  return pathname.replace(/^\/o\/[^/]+\//, `/o/${canonicalSlug}/`);
}

export function loginPath(returnTo: string): string {
  const safe = safeReturnPath(returnTo);
  return safe ? `/login?returnTo=${encodeURIComponent(safe)}` : "/login";
}
